import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { useBankList } from "../../../../../../app/hooks/useBankList";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { ContentWrapper, InputWrapper, Error, ButtonsWrapper } from "../../../shared/styles";
import { Input } from "../../../../../../components/common/Input";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { InputPhone } from "../../../../../../components/common/InputPhone";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";
import { CountryConfig } from "../../../shared/types";
import { FormInputs } from "../../../../../../types/walletTypes";

interface IndiaBeneficiaryFormProps {
  config?: CountryConfig;
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const IndiaBeneficiaryForm: React.FC<IndiaBeneficiaryFormProps> = ({ handleNext, handlePrevious }) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData: FormInputs = useAppSelector(
    (state) => (state.formData as any)?.asiaPayoutData?.inr || {}
  );
  const { businessId } = useAppSelector((state) => state.users);

  const { banks, selectBanks, loading: banksLoading } = useBankList({
    country: "INR",
    businessId,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: {
      accountHolderName: asiaPayoutData?.accountHolderName || "",
      ifscCode: asiaPayoutData?.ifscCode || "",
      bankName: asiaPayoutData?.bankName || "",
      accountNumber: asiaPayoutData?.accountNumber || "",
      branchName: asiaPayoutData?.branchName || "",
      receiverMobileNumber: asiaPayoutData?.receiverMobileNumber || "",
    },
    mode: "onChange",
  });

  const watchBankName = watch("bankName");
  const selectedBank = (banks as any[])?.find((b: any) => b.name === watchBankName);
  const ifscPrefix = selectedBank ? `${selectedBank.ifsc || ""}0`.toUpperCase() : "";

  const handleIfscChange = (
    valOrEvent: any,
    onChange: (value: string) => void
  ) => {
    const rawVal = typeof valOrEvent === "string" ? valOrEvent : valOrEvent?.target?.value || "";
    let val = rawVal.toUpperCase().replace(/[^A-Z0-9]/g, "");

    if (ifscPrefix && val.startsWith(ifscPrefix)) {
      val = val.substring(ifscPrefix.length);
    }

    const isFullIfscFormat = val.length === 11 && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(val);
    if (val.length > 6 && !isFullIfscFormat) {
      val = val.slice(0, 6);
    }

    onChange(val);
  };

  useEffect(() => {
    if (ifscPrefix && asiaPayoutData?.ifscCode) {
      if (asiaPayoutData.ifscCode.toUpperCase().startsWith(ifscPrefix)) {
        const remaining = asiaPayoutData.ifscCode.substring(ifscPrefix.length);
        setValue("ifscCode", remaining, { shouldValidate: true });
      }
    }
  }, [ifscPrefix, asiaPayoutData?.ifscCode, setValue]);

  const onSubmit = (data: FormInputs) => {
    const rawIfsc = (data.ifscCode || "").trim().toUpperCase();
    const fullIfsc = `${ifscPrefix}${rawIfsc}`;

    const updatedData = {
      ...asiaPayoutData,
      ...data,
      ifscCode: fullIfsc,
    };
    dispatch(savedAsiaPayoutData({ country: "inr", data: updatedData }));
    handleNext?.();
  };

  return (
    <ContentWrapper>
      <Loader isLoading={banksLoading} />

      <InputWrapper>
        <Controller
          name="accountHolderName"
          control={control}
          rules={{ required: "Beneficiary name is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Beneficiary name"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors.accountHolderName}
            />
          )}
        />
        {errors.accountHolderName?.message && (
          <Error>{String(errors.accountHolderName.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="bankName"
          control={control}
          rules={{ required: "Bank name is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              value={field.value || undefined}
              onChange={(val) => {
                field.onChange(val);
                setValue("ifscCode", "", { shouldValidate: true });
              }}
              options={selectBanks.map((b) => ({ label: b.label, value: b.label }))}
              placeholder={banksLoading ? "Loading banks..." : "Bank name"}
              lightBorder
            />
          )}
        />
        {errors.bankName?.message && (
          <Error>{String(errors.bankName.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="ifscCode"
          control={control}
          rules={{
            required: "IFSC code is required",
            validate: {
              validSuffix: (val) => {
                const cleanVal = (val || "").trim().toUpperCase();
                if (cleanVal.length !== 6) {
                  if (cleanVal.length === 11 && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(cleanVal)) {
                    return "Pasted IFSC code belongs to a different bank";
                  }
                  return "IFSC code suffix must be exactly 6 characters";
                }
                if (!/^[A-Z0-9]{6}$/.test(cleanVal)) {
                  return "IFSC code suffix must contain only letters and numbers";
                }
                const fullIfsc = `${ifscPrefix}${cleanVal}`;
                if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(fullIfsc)) {
                  return "Invalid IFSC code format";
                }
                return true;
              },
            },
          }}
          render={({ field }) => (
            <Input
              type="text"
              label="IFSC code"
              value={field.value || ""}
              onChange={(e) => handleIfscChange(e, field.onChange)}
              disabled={!watchBankName || !ifscPrefix}
              disableLabel
              showCountryPhoneCode={!!ifscPrefix}
              countryPhoneCode={ifscPrefix}
              paddingForPrefix={ifscPrefix && "4.5rem"}
              hasError={!!errors.ifscCode}
            />
          )}
        />
        {errors.ifscCode?.message && <Error>{String(errors.ifscCode.message)}</Error>}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="accountNumber"
          control={control}
          rules={{
            required: "Account number is required",
            pattern: {
              value: /^\d{9,18}$/,
              message: "Account number must be between 9 and 18 digits",
            },
          }}
          render={({ field }) => (
            <Input
              type="text"
              inputMode="numeric"
              label="Account number"
              value={field.value || ""}
              onChange={(e: any) => {
                const val = typeof e === "string" ? e : e?.target?.value || "";
                field.onChange(val.replace(/\D/g, "").slice(0, 18));
              }}
              hasError={!!errors.accountNumber}
            />
          )}
        />
        {errors.accountNumber?.message && (
          <Error>{String(errors.accountNumber.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="branchName"
          control={control}
          rules={{ required: "Branch name is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Branch name"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors.branchName}
            />
          )}
        />
        {errors.branchName?.message && (
          <Error>{String(errors.branchName.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="receiverMobileNumber"
          control={control}
          rules={{ required: "Phone number is required" }}
          render={({ field }) => (
            <InputPhone
              label="Phone number"
              field={field}
              defaultCountry="IN"
              showPhoneCode={true}
              hasError={!!errors.receiverMobileNumber}
            />
          )}
        />
        {errors.receiverMobileNumber?.message && (
          <Error>{String(errors.receiverMobileNumber.message)}</Error>
        )}
      </InputWrapper>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={!isValid} label="Continue" height="48px" />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default IndiaBeneficiaryForm;
